import {
  faSitemap,
  faBoxOpen,
  faThumbtack,
  faStore,
  faBuilding,
  faUsers,
  faClipboardList,
  faClipboardCheck,
  faFileInvoiceDollar,
  faCreditCard,
  faTags,
  faLink,
  faSlidersH,
  faSpider,
  faBullhorn,
  faFlashlight,
  faNewspaper,
  faConciergeBell,
  faGem,
  faCompass,
  faSeedling,
  faThumbsUp,
  faTshirt,
  faReceipt,
  faRobot,
  faKey,
  faBell,
  faComment,
  faCalendarCheck,
  faHandshake,
  faTasks,
  faChartLine,
  faComments,
  faRocket,
  faBookOpen,
  faBrain,
  faGraduationCap,
  faTerminal,
  faHistory,
  faLightbulb,
  faShoppingBag,
  faLayerGroup,
  faBlender,
  faStoreSlash,
  faVideo,
  faSync,
  faSparkles,
  faBadgePercent
} from '@fortawesome/pro-light-svg-icons';

import { ADMIN_SCOPES } from './admin_scope_constants';

// Shared dnd-kit droppable id for the drag-to-remove zone (AdminNav + AdminNavTrash must agree).
export const NAV_TRASH_ID = 'nav-trash';

// Single source of truth for admin navigation destinations.
// `scopes: []` is visible to every admin; otherwise the admin needs at least one (ALL always passes).
export const ADMIN_NAV_ITEMS = [
  // Catalog
  {
    key: 'catalogs',
    label: 'Catalogs',
    path: '/catalogs',
    icon: faBoxOpen,
    description: 'Brand catalogs: configuration, review, and release.',
    scopes: [ADMIN_SCOPES.MANAGE_CATALOG]
  },
  {
    key: 'hierarchy',
    label: 'Hierarchy',
    path: '/hierarchy',
    icon: faSitemap,
    description: 'Browse and edit the product category taxonomy.',
    scopes: [ADMIN_SCOPES.MANAGE_CATALOG]
  },
  {
    key: 'products',
    label: 'Products',
    path: '/products',
    icon: faBoxOpen,
    description: 'Search, match, and edit catalog products.',
    scopes: [ADMIN_SCOPES.MANAGE_CATALOG, ADMIN_SCOPES.EDIT_PRODUCT, ADMIN_SCOPES.MATCH_PRODUCT]
  },
  {
    key: 'pins',
    label: 'Pins',
    path: '/pins',
    icon: faThumbtack,
    description: 'Review and manage pinned product links.',
    scopes: [ADMIN_SCOPES.MANAGE_CATALOG]
  },
  {
    key: 'merchants',
    label: 'Merchants',
    path: '/merchants',
    icon: faStore,
    description: 'Manage merchant and retailer records.',
    scopes: [ADMIN_SCOPES.MANAGE_CATALOG]
  },
  {
    key: 'merchants-v2',
    label: 'Merchants V2',
    path: '/merchantsV2',
    icon: faStore,
    description: 'Merchants with locks, network comparisons, and auto-composed commission disclaimers.',
    scopes: [ADMIN_SCOPES.MANAGE_CATALOG]
  },
  {
    key: 'merchant-commission-disclaimers',
    label: 'Commission Disclaimers',
    path: '/merchant-commission-disclaimers',
    icon: faBadgePercent,
    description: 'Browse the network commission terms stored per merchant program and the disclaimer text derived from them.',
    scopes: [ADMIN_SCOPES.MANAGE_CATALOG]
  },
  {
    key: 'product-mentions',
    label: 'Product Mentions',
    path: '/product-mentions',
    icon: faTags,
    description: 'See the products the detection pipeline identifies in social posts.',
    scopes: [ADMIN_SCOPES.MANAGE_CATALOG]
  },

  // Support / User management
  {
    key: 'private-offers',
    label: 'Private Offers',
    path: '/private-offers',
    icon: faBuilding,
    description: 'Review Private Offer terms, score their value, and approve or request revisions.',
    scopes: [ADMIN_SCOPES.MANAGE_USERS, ADMIN_SCOPES.MANAGE_BRANDS]
  },
  {
    key: 'brands',
    label: 'Brands',
    path: '/brands',
    icon: faBuilding,
    description: 'Manage brand accounts and their settings.',
    scopes: [ADMIN_SCOPES.MANAGE_USERS]
  },
  {
    key: 'brand-billing',
    label: 'Brand Billing',
    path: '/brand-billing',
    icon: faCreditCard,
    description: 'How a brand is billed across commissions, subscription, budget invoices and Spotlights.',
    scopes: [ADMIN_SCOPES.MANAGE_INVOICING]
  },
  {
    key: 'brand-cancellations',
    label: 'Brand Cancellations',
    path: '/brand-cancellations',
    icon: faStoreSlash,
    description: 'Preview, schedule, and run brand offboarding cleanup.',
    scopes: [ADMIN_SCOPES.MANAGE_USERS, ADMIN_SCOPES.MANAGE_BRANDS]
  },
  {
    key: 'brand-tracking-health',
    label: 'Brand Tracking Health',
    path: '/brand-tracking-health',
    icon: faChartLine,
    description: 'Cross-brand tracking integration health — what is broken, for which brand, and why.',
    scopes: [ADMIN_SCOPES.EDIT_BRAND]
  },
  {
    key: 'users',
    label: 'Users',
    path: '/users',
    icon: faUsers,
    description: 'Search and administer creator and user accounts.',
    scopes: [ADMIN_SCOPES.MANAGE_USERS]
  },
  {
    key: 'waitlist',
    label: 'Waitlist',
    path: '/waitlist',
    icon: faClipboardList,
    description: 'Review and decision user waitlist applications.',
    scopes: [ADMIN_SCOPES.MANAGE_USERS]
  },
  {
    key: 'brand-onboarding',
    label: 'Brand Onboarding',
    path: '/brand-onboarding',
    icon: faClipboardCheck,
    description: 'Review and approve brand onboarding requests.',
    scopes: [ADMIN_SCOPES.MANAGE_USERS]
  },
  {
    key: 'invoices',
    label: 'Invoices',
    path: '/invoices',
    icon: faFileInvoiceDollar,
    description: 'View and manage brand invoices and billing.',
    scopes: [ADMIN_SCOPES.MANAGE_USERS]
  },
  {
    key: 'spotlights',
    label: 'Spotlights',
    path: '/spotlights',
    icon: faFlashlight,
    description: 'Cross-brand & creator Spotlights performance, billing, trials, and ad automation.',
    scopes: [ADMIN_SCOPES.MANAGE_SPOTLIGHTS]
  },
  {
    key: 'subscriptions',
    label: 'Subscriptions',
    path: '/subscriptions',
    icon: faCreditCard,
    description: 'Manage subscription plans, offers, and bundles.',
    scopes: [ADMIN_SCOPES.MANAGE_USERS]
  },

  // Matchers
  {
    key: 'all-brands',
    label: 'All Brands',
    path: '/all-brands',
    icon: faTags,
    description: 'Browse every brand for product matching.',
    scopes: [ADMIN_SCOPES.EDIT_BRAND]
  },
  {
    key: 'url-data',
    label: 'URL Data',
    path: '/url-data',
    icon: faLink,
    description: 'Inspect and match scraped URL link data.',
    scopes: [ADMIN_SCOPES.MATCH_PRODUCT, ADMIN_SCOPES.MANAGE_CATALOG]
  },
  {
    key: 'links-to-check',
    label: 'Links to Check',
    path: '/links-to-check',
    icon: faClipboardCheck,
    description: 'Answer the links our matcher could not settle on its own, one card at a time.',
    scopes: [ADMIN_SCOPES.REVIEW_HELD_LINKS]
  },
  {
    key: 'catalog-tool',
    label: 'Catalog Tool',
    path: '/catalog-tool',
    icon: faLink,
    description: 'Bulk Tools, Bulk Manage, Explore, Product Sinks, and Reports for catalog URLs.',
    scopes: [ADMIN_SCOPES.MANAGE_CATALOG]
  },
  {
    key: 'url-site-rules',
    label: 'URL Site Rules',
    path: '/url-site-rules',
    icon: faSlidersH,
    description: 'Configure per-site scraping and parsing rules.',
    scopes: [ADMIN_SCOPES.MANAGE_CATALOG]
  },
  {
    key: 'scrapers',
    label: 'Scrapers',
    path: '/scrapers',
    icon: faSpider,
    description: 'Monitor and manage product scrapers.',
    scopes: [ADMIN_SCOPES.MANAGE_CATALOG]
  },

  // Marketing
  {
    key: 'announcements',
    label: 'Announcements',
    path: '/announcements',
    icon: faBullhorn,
    description: 'Create and publish in-app announcements.',
    scopes: [ADMIN_SCOPES.MANAGE_ANNOUNCEMENTS]
  },
  {
    key: 'newsletters',
    label: 'Newsletter',
    path: '/newsletters',
    icon: faNewspaper,
    description: 'Compose and send creator newsletters.',
    scopes: [ADMIN_SCOPES.MANAGE_NEWSLETTERS]
  },
  {
    key: 'sales',
    label: 'Sales and Promotions',
    path: '/sales',
    icon: faTags,
    description: 'Search public brand sales, manage featured placement, and hide sales from creators.',
    scopes: [ADMIN_SCOPES.MANAGE_NEWSLETTERS, ADMIN_SCOPES.MANAGE_MARKETING, ADMIN_SCOPES.MANAGE_CATALOG]
  },
  {
    key: 'new-products',
    label: 'New Products',
    path: '/new-products',
    icon: faSparkles,
    description: 'Hide and unhide newly published partner products in the creator New Products tab.',
    scopes: [ADMIN_SCOPES.MANAGE_PRODUCT_DROPS]
  },
  {
    key: 'webinars',
    label: 'Webinars',
    path: '/webinars',
    icon: faVideo,
    description: 'Create and publish webinars for the public webinar hub.',
    scopes: [ADMIN_SCOPES.MANAGE_WEBINARS, ADMIN_SCOPES.MANAGE_MARKETING]
  },
  {
    key: 'user-tips',
    label: 'User Tips',
    path: '/user-tips',
    icon: faLightbulb,
    description: 'Configure tag-targeted tips shown in the creator dashboard and mobile app.',
    scopes: [ADMIN_SCOPES.MANAGE_USER_TIPS]
  },
  {
    key: 'notifications',
    label: 'Notifications',
    path: '/notifications',
    icon: faBell,
    description: 'Inspect notification delivery, settings and templates.',
    scopes: [ADMIN_SCOPES.MANAGE_MARKETING, ADMIN_SCOPES.MANAGE_USERS]
  },
  {
    key: 'shopmy-dm',
    label: 'ShopMy DM',
    path: '/shopmy-dm',
    icon: faComment,
    description: 'Look up one creator’s ShopMy DM log and see why a comment did or didn’t send.',
    scopes: [ADMIN_SCOPES.MANAGE_USERS]
  },

  // Concierge / Noir / Discover
  {
    key: 'moments',
    label: 'Moments',
    path: '/moments',
    icon: faShoppingBag,
    description: 'Agent-built Moments: observe builds, curate, and take them live.',
    scopes: [ADMIN_SCOPES.MANAGE_USERS]
  },
  {
    key: 'concierge',
    label: 'Concierge',
    path: '/concierge',
    icon: faConciergeBell,
    description: 'Manage concierge requests and talent outreach.',
    scopes: [ADMIN_SCOPES.MANAGE_USERS]
  },
  {
    key: 'noir',
    label: 'Noir',
    path: '/noir',
    icon: faGem,
    description: 'Manage Noir membership requests and members.',
    scopes: [ADMIN_SCOPES.MANAGE_USERS]
  },
  {
    key: 'discover',
    label: 'Discover',
    path: '/discover',
    icon: faCompass,
    description: 'Curate Discover brand lists and placements.',
    scopes: [ADMIN_SCOPES.MANAGE_USERS]
  },

  // Talent
  {
    key: 'greenhouse',
    label: 'Greenhouse',
    path: '/greenhouse',
    icon: faSeedling,
    description: 'Review talent applications synced from Greenhouse.',
    scopes: [ADMIN_SCOPES.MANAGE_GREENHOUSE]
  },

  {
    key: 'circles',
    label: 'Featured Content',
    path: '/circles',
    icon: faUsers,
    description: 'Manage Circles, onboarding brands, and featured placements.',
    scopes: [ADMIN_SCOPES.MANAGE_RECOMMENDATIONS]
  },

  // Recommendations
  {
    key: 'recommendations',
    label: 'Recommendations',
    path: '/recommendations',
    icon: faThumbsUp,
    description: 'Build and edit homepage recommendation grids.',
    scopes: [ADMIN_SCOPES.MANAGE_RECOMMENDATIONS]
  },
  {
    key: 'wheel',
    label: 'Wheel',
    path: '/wheel',
    icon: faSync,
    description: 'Upload home-wheel hero videos, tag them, and spin up new terms.',
    scopes: [ADMIN_SCOPES.MANAGE_RECOMMENDATIONS]
  },

  // Shopper search
  {
    key: 'collection-ranking',
    label: 'Collection Ranking',
    path: '/collection-ranking',
    icon: faLayerGroup,
    description: 'Review LLM quality scores gating collections into the search index.',
    scopes: [ADMIN_SCOPES.MANAGE_SHOPPER_SEARCH]
  },
  {
    key: 'blend-search',
    label: 'Blend Search',
    path: '/blend-search',
    icon: faBlender,
    description: 'Preview the app home feed for any user — the live blended product and collection recommendations.',
    scopes: [ADMIN_SCOPES.MANAGE_SHOPPER_SEARCH]
  },

  // Style audit
  {
    key: 'style-audit',
    label: 'Style Audit',
    path: '/style-audit',
    icon: faTshirt,
    description: 'Audit creator style profiles and tagging.',
    scopes: [ADMIN_SCOPES.VIEW_STYLE_AUDIT]
  },

  // Order adjustments
  {
    key: 'manual-order-adjustments',
    label: 'Order Adjustments',
    path: '/manual-order-adjustments',
    icon: faReceipt,
    description: 'Create manual orders and void creator commissions.',
    scopes: [ADMIN_SCOPES.WRITE_MANUAL_ORDER_ADJUSTMENTS]
  },

  // Pin-match silent eval
  {
    key: 'pin-match-silent-eval',
    label: 'Agentic Pin Match Review',
    path: '/pin-match-silent-eval',
    icon: faRobot,
    description: "Review the pin-match agent's catalog matches, and create the products the catalog team confirmed.",
    scopes: [ADMIN_SCOPES.PIN_MATCH_AGENTS]
  },

  // OAuth
  {
    key: 'oauth',
    label: 'OAuth',
    path: '/oauth',
    icon: faKey,
    description: 'Manage OAuth applications and credentials.',
    scopes: [ADMIN_SCOPES.MANAGE_OAUTH_APPLICATIONS]
  },

  // Super admin (ALL scope only)
  {
    key: 'consults',
    label: 'Consults',
    path: '/consults',
    icon: faCalendarCheck,
    description: 'Review consult bookings and requests.',
    scopes: [ADMIN_SCOPES.ALL]
  },
  {
    key: 'collaborations',
    label: 'Collaborations',
    path: '/collaborations',
    icon: faHandshake,
    description: 'Manage brand collaborations and contracts.',
    scopes: [ADMIN_SCOPES.ALL]
  },
  {
    key: 'contract-task-templates',
    label: 'Tasks',
    path: '/contract-task-templates',
    icon: faTasks,
    description: 'Edit contract task templates.',
    scopes: [ADMIN_SCOPES.ALL]
  },
  {
    key: 'analytics',
    label: 'Analytics',
    path: '/analytics',
    icon: faChartLine,
    description: 'View platform analytics dashboards.',
    scopes: [ADMIN_SCOPES.ALL]
  },
  {
    key: 'chat',
    label: 'Chat',
    path: '/chat',
    icon: faComments,
    description: 'Monitor and manage chat conversations.',
    scopes: [ADMIN_SCOPES.ALL]
  },
  {
    key: 'growthscraper',
    label: 'Growth',
    path: '/growthscraper',
    icon: faRocket,
    description: 'Run growth scraping and prospecting tools.',
    scopes: [ADMIN_SCOPES.ALL]
  },
  {
    key: 'lookbookorders',
    label: 'Lookbooks',
    path: '/lookbookorders',
    icon: faBookOpen,
    description: 'Manage lookbook orders and fulfillment.',
    scopes: [ADMIN_SCOPES.ALL]
  },
  {
    key: 'ml',
    label: 'Machine Learning',
    path: '/ml',
    icon: faBrain,
    description: 'Inspect ML models and pipelines.',
    scopes: [ADMIN_SCOPES.ALL]
  },
  {
    key: 'creator-feedback',
    label: 'Creator Feedback',
    path: '/creator-feedback',
    icon: faClipboardList,
    description: 'Review CSM creator removals and discovery feedback across strategies.',
    scopes: [ADMIN_SCOPES.ALL]
  },
  {
    key: 'ml-training',
    label: 'Ml Training',
    path: '/ml-training',
    icon: faGraduationCap,
    description: 'Manage ML training datasets and runs.',
    scopes: [ADMIN_SCOPES.ALL]
  },
  {
    key: 'prompts',
    label: 'Prompts',
    path: '/prompts',
    icon: faTerminal,
    description: 'Edit and version AI prompts.',
    scopes: [ADMIN_SCOPES.ALL]
  },
  {
    key: 'script-config',
    label: 'ScriptConfig',
    path: '/script-config',
    icon: faTerminal,
    description: 'Manage runnable script configurations and argument fields.',
    scopes: [ADMIN_SCOPES.ALL]
  },
  {
    key: 'scripts',
    label: 'Scripts',
    path: '/scripts',
    icon: faTerminal,
    description: 'Run permitted scripts and view script run history.',
    scopes: [ADMIN_SCOPES.RUN_SCRIPTS]
  },
  // Always available
  {
    key: 'changelog',
    label: 'Changelog',
    path: '/changelog',
    icon: faHistory,
    description: 'View the admin changelog and release notes.',
    scopes: []
  }
];
